import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  Paper, TextInput, PasswordInput, Button, Text, Center, Stack,
  ActionIcon, useMantineColorScheme, useComputedColorScheme, ThemeIcon,
  Group, Anchor,
} from '@mantine/core'
import { useForm, Controller } from 'react-hook-form'
import { IconSun, IconMoon, IconLeaf } from '@tabler/icons-react'
import { useAuth } from '@/context/AuthContext'

interface RegisterFormValues {
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
}

export default function Register() {
  const [error, setError] = useState('')
  const { register } = useAuth()
  const { setColorScheme } = useMantineColorScheme()
  const computedColorScheme = useComputedColorScheme('light')
  const isDark = computedColorScheme === 'dark'
  const navigate = useNavigate()

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormValues>({
    defaultValues: { firstName: '', lastName: '', email: '', password: '', confirmPassword: '' },
  })

  const onSubmit = handleSubmit(async (values) => {
    setError('')
    try {
      await register(values.firstName, values.lastName, values.email, values.password)
      navigate('/')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Бүртгүүлэхэд алдаа гарлаа')
    }
  })

  return (
    <Center h="100vh" style={{ backgroundColor: isDark ? 'var(--mantine-color-dark-8)' : 'var(--mantine-color-gray-0)' }}>
      <div style={{ position: 'absolute', top: 16, right: 16 }}>
        <ActionIcon
          variant="subtle"
          onClick={() => setColorScheme(isDark ? 'light' : 'dark')}
          size="lg"
          radius="md"
          color={isDark ? 'yellow' : 'blue'}
        >
          {isDark ? <IconSun size={18} /> : <IconMoon size={18} />}
        </ActionIcon>
      </div>
      <Paper shadow="md" p="xl" w={440} radius="md">
        <Center mb="md">
          <Group gap="xs">
            <ThemeIcon size={44} radius="md" variant="gradient" gradient={{ from: 'green', to: 'teal', deg: 135 }}>
              <IconLeaf size={26} stroke={1.5} />
            </ThemeIcon>
          </Group>
        </Center>
        <Text fw={700} size="lg" ta="center" mb="xs">NatureMed</Text>
        <Text size="sm" c="dimmed" ta="center" mb="lg">Шинэ бүртгэл үүсгэх</Text>

        <form onSubmit={onSubmit}>
          <Stack>
            <Group grow>
              <Controller
                name="lastName"
                control={control}
                rules={{
                  validate: (v) => (v.trim().length >= 2 ? true : 'Овог хамгийн багадаа 2 тэмдэгт байх ёстой'),
                }}
                render={({ field }) => (
                  <TextInput label="Овог" placeholder="Овог" {...field} error={errors.lastName?.message} />
                )}
              />
              <Controller
                name="firstName"
                control={control}
                rules={{
                  validate: (v) => (v.trim().length >= 2 ? true : 'Нэр хамгийн багадаа 2 тэмдэгт байх ёстой'),
                }}
                render={({ field }) => (
                  <TextInput label="Нэр" placeholder="Нэр" {...field} error={errors.firstName?.message} />
                )}
              />
            </Group>
            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Имэйл хаяг буруу байна',
                pattern: { value: /^\S+@\S+\.\S+$/, message: 'Имэйл хаяг буруу байна' },
              }}
              render={({ field }) => (
                <TextInput label="Имэйл" placeholder="admin@naturemed.mn" {...field} error={errors.email?.message} />
              )}
            />
            <Controller
              name="password"
              control={control}
              rules={{
                required: 'Нууц үг хамгийн багадаа 8 тэмдэгт байх ёстой',
                minLength: { value: 8, message: 'Нууц үг хамгийн багадаа 8 тэмдэгт байх ёстой' },
              }}
              render={({ field }) => (
                <PasswordInput label="Нууц үг" placeholder="Хамгийн багадаа 8 тэмдэгт" {...field} error={errors.password?.message} />
              )}
            />
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                validate: (v) => (v === getValues('password') ? true : 'Нууц үг таарахгүй байна'),
              }}
              render={({ field }) => (
                <PasswordInput label="Нууц үг давтах" placeholder="Нууц үгээ дахин оруулна уу" {...field} error={errors.confirmPassword?.message} />
              )}
            />
            {error && <Text size="sm" c="red">{error}</Text>}
            <Button type="submit" fullWidth loading={isSubmitting} disabled={isSubmitting}>Бүртгүүлэх</Button>
            <Text size="sm" c="dimmed" ta="center">
              Бүртгэлтэй юу?{' '}
              <Anchor component={Link} to="/login" size="sm">Нэвтрэх</Anchor>
            </Text>
          </Stack>
        </form>
      </Paper>
    </Center>
  )
}
